/**
 * MessageAttachments — renders the attachments shown under a USER turn (chat
 * document attachments, Phase 6.2).
 *
 * Accepts the persisted `MessageAttachment[]` shape (snake_case wire JSON, no
 * transform); the composer maps a live `AttachmentDraft` to the SAME shape on
 * send (`MessageInput.draftToDisplay`) so ONE component renders both the
 * just-sent and the rehydrated turn.
 *
 * Layout (a net-new state — the prototype chat screen does not depict in-bubble
 * attachments, so this is drafted consistent with the AttachmentChips visual
 * language and the `--g-*` token system; flagged in the PR per the design drift
 * rule): a flex-wrap row of
 *   • image → a thumbnail button that opens `file_url` in a new tab (lightbox is
 *     a fast-follow); falls back to the larger `file_url` when no thumb exists.
 *   • pdf/xlsx/csv → a file card (file icon + filename + doc-type label + page
 *     count) that opens `file_url`.
 * When `file_url` is null (a just-sent live attachment whose presigned GET is
 * not known yet) the tile is a non-interactive `<div>` — it becomes a link once
 * the thread reloads with the persisted attachment.
 *
 * Icon note: `@garden/ui` has no `image`/`fileText`/`sheet` glyph, so the file
 * card uses `clip` (Paperclip) — the same substitution AttachmentChips uses.
 *
 * Tokens via the `--g-*` mirror; ≥44px tap area; filename/doc-type are DB/user
 * text rendered AS text. All labels via the `chat` i18n namespace.
 */
import { Icon } from "@garden/ui";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { MessageAttachment } from "@/features/chat/attachment-types";

export interface MessageAttachmentsProps {
  attachments: MessageAttachment[];
}

/** Localized doc-type label (RU fallback) — `t('docType.<doc_type>')`. */
function useDocTypeLabel(): (
  docType: MessageAttachment["doc_type"],
) => string | null {
  const { t } = useTranslation("chat");
  return (docType) => (docType !== null ? t(`docType.${docType}`) : null);
}

function ImageTile({ a }: { a: MessageAttachment }): ReactElement {
  const { t } = useTranslation("chat");
  const src = a.thumb_url ?? a.file_url;
  const alt = a.filename ?? t("attach.listLabel");
  const img =
    src !== null ? (
      <img
        src={src}
        alt={alt}
        className="size-full rounded-[var(--g-radius)] object-cover"
      />
    ) : (
      <span className="grid size-full place-items-center rounded-[var(--g-radius)] bg-secondary text-primary">
        <Icon name="clip" size={18} />
      </span>
    );

  const className =
    "block size-16 overflow-hidden rounded-[var(--g-radius)] border border-border bg-background";

  if (a.file_url !== null) {
    return (
      <a
        href={a.file_url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={alt}
        data-testid={`chat-attachment-${a.id}`}
        className={
          className +
          " transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        }
      >
        {img}
      </a>
    );
  }
  return (
    <div
      data-testid={`chat-attachment-${a.id}`}
      className={className}
      aria-label={alt}
    >
      {img}
    </div>
  );
}

function FileTile({ a }: { a: MessageAttachment }): ReactElement {
  const { t } = useTranslation("chat");
  const docTypeLabel = useDocTypeLabel()(a.doc_type);

  const inner = (
    <span className="flex min-h-11 items-center gap-2.5 px-3 py-2">
      <span className="grid size-8 shrink-0 place-items-center rounded bg-secondary text-primary">
        <Icon name="clip" size={16} />
      </span>
      <span className="min-w-0">
        <span className="block max-w-[12rem] truncate text-[14px] font-semibold text-foreground">
          {a.filename ?? t("docType.other")}
        </span>
        {docTypeLabel !== null || a.page_count !== null ? (
          <span className="mt-0.5 flex items-center gap-1.5 text-[12px] tabular-nums text-muted-foreground">
            {docTypeLabel !== null ? <span>{docTypeLabel}</span> : null}
            {docTypeLabel !== null && a.page_count !== null ? (
              <span aria-hidden="true">·</span>
            ) : null}
            {a.page_count !== null ? <span>{a.page_count}</span> : null}
          </span>
        ) : null}
      </span>
    </span>
  );

  const className =
    "inline-flex max-w-full rounded-[var(--g-radius)] border border-border bg-background";

  if (a.file_url !== null) {
    return (
      <a
        href={a.file_url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={a.filename ?? t("docType.other")}
        data-testid={`chat-attachment-${a.id}`}
        className={
          className +
          " transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        }
      >
        {inner}
      </a>
    );
  }
  return (
    <div
      data-testid={`chat-attachment-${a.id}`}
      className={className}
      aria-label={a.filename ?? t("docType.other")}
    >
      {inner}
    </div>
  );
}

export function MessageAttachments({
  attachments,
}: MessageAttachmentsProps): ReactElement | null {
  const { t } = useTranslation("chat");
  if (attachments.length === 0) return null;

  return (
    <div
      className="mb-1.5 flex flex-wrap justify-end gap-2"
      data-testid="chat-message-attachments"
      aria-label={t("attach.listLabel")}
    >
      {attachments.map((a) =>
        a.kind === "image" ? (
          <ImageTile key={a.id} a={a} />
        ) : (
          <FileTile key={a.id} a={a} />
        ),
      )}
    </div>
  );
}

export default MessageAttachments;
